import { useDisclosure, useRouteMatchParam, useWindowSize } from '@/hooks';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Layout,
  Modal,
  Popover,
  Row,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import logo from '@/assets/images/Logo_Footer.png';
import { useWorkOrderByGuid } from '@/core/domain/workorder/QRCode/QRCode.hooks';
import { ColumnsType, Group } from '@/components/_common';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import StatusTag from '@/components/_common/StatusTag';
import {
  workorderComplementColor,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useIntl } from 'react-intl';
import moment from 'moment';
import formatterPrice from '@/utils/number/formatterPrice';
import formatterPercent from '@/utils/number/formatterPercent';
import './styles.less';
import ReactSignatureCanvas from 'react-signature-canvas';
import { convertBase64ToFile } from '@/utils/convertBase64ToFile';
import { useWorkOrderComplementSignaturePublic } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { ChevronLeft, ChevronRight, MessageCircle } from 'react-feather';
import { SubDirectoryEnum } from '@/core/domain/archive/Archive/Archive.types';
import { useHistory } from 'react-router-dom';
import {
  WorkOrderItemRefusePublicRequest,
  WorkOrderItemType,
  workorderTypeColor,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useArchiveUpload } from '@/core/domain/archive/Archive/Archive.hooks';
import { InfoCircleOutlined } from '@ant-design/icons';
import { MathRound } from '@/utils/functions/MathRound';
import {
  WorkOrderComplementObservations,
  WorkOrderQRCodeItem,
} from '@/core/domain/workorder/QRCode/QRCode.types';
import RefuseIcon from '@/components/_common/RefuseIcon';
import ItemRefusePublicModal from '@/components/Public/WorkOrderComplement/ItemRefusePublicModal';

export default function PublicWorkOrderComplement() {
  const guid = useRouteMatchParam('guid');
  const intl = useIntl();
  const sig = useRef<ReactSignatureCanvas>(null);

  const [signatureOpen, signatureAction] = useDisclosure(false);
  const [authorizeComplement, setAuthorizeComplement] =
    useState<boolean>(false);
  const [refuseItem, setRefuseItem] =
    useState<WorkOrderItemRefusePublicRequest>();

  const { data: workorder, dataUpdatedAt } = useWorkOrderByGuid(guid);

  const uploadArchive = useArchiveUpload();

  const workorderComplementSignature = useWorkOrderComplementSignaturePublic();

  const isLoading =
    uploadArchive.isLoading || workorderComplementSignature.isLoading;

  const { isMobile } = useWindowSize();

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (workorder) {
      const complementIndex = workorder?.complements.findIndex(
        (complement) => complement.workOrderGuid === guid,
      );

      const index =
        typeof complementIndex === 'number' && complementIndex >= 0
          ? complementIndex
          : 0;

      setCurrent(index);
    }
  }, [dataUpdatedAt]);

  const hasPrev = current > 0;
  const hasNext = current < (workorder?.complements.length || 1) - 1;

  const complement = workorder?.complements[current];

  const hasSignature = !!complement?.authorizedSignature;

  const history = useHistory();

  const field = (label: string, value: ReactNode, column = false) => {
    column = isMobile || column;

    return (
      <Group
        className="workorder-field"
        style={{ flexDirection: column ? 'column' : 'row' }}
        align={column ? 'start' : 'center'}
        position="between"
      >
        <span style={{ color: 'darkgray', fontWeight: 500 }}>{label}</span>
        <div>
          {typeof value === 'string' ? <strong>{value}</strong> : value}
        </div>
      </Group>
    );
  };

  const handleClose = () => {
    sig.current?.clear();
    signatureAction.close();
  };

  const handleSubmit = useCallback(async () => {
    try {
      if (!sig.current || sig.current?.isEmpty()) {
        return message.warn('Preencha a assinatura');
      }

      if (!complement?.workOrderGuid) return;

      const imageStr = sig.current.getTrimmedCanvas().toDataURL('image/png');
      const imageFile = await convertBase64ToFile(
        imageStr,
        new Date().toISOString(),
      );

      const upload = await uploadArchive.mutateAsync({
        file: [imageFile],
        isPublic: false,
        extension: 'png',
        subdirectory: SubDirectoryEnum.Signatures,
      });

      await workorderComplementSignature.mutateAsync({
        complementGuid: complement.workOrderGuid,
        signature: upload.filePath,
        authorizeComplement,
      });

      handleClose();

      history.push(`/r/os/${complement.workOrderGuid}`);
    } catch {}
  }, [sig.current, complement, authorizeComplement]);

  const complementItemsPartsInternal =
    complement?.items.filter(
      (item) => item.isInternal && item.type === WorkOrderItemType.Part,
    ) || [];

  const complementItemsServices =
    complement?.items.filter(
      (item) => item.type === WorkOrderItemType.Service,
    ) || [];

  const items = [...complementItemsPartsInternal, ...complementItemsServices];

  const columnRefuse: ColumnsType<WorkOrderQRCodeItem> =
    complement?.status === WorkOrderComplementStatus.Open
      ? [
          {
            title: 'Recusar',
            className: 'table-th',
            align: 'center',
            render: (_, item) => (
              <RefuseIcon
                onClick={() =>
                  setRefuseItem({
                    complementId: complement.id,
                    workOrderItemId: item.id,
                  })
                }
              />
            ),
          },
        ]
      : [];

  const columnsItems: ColumnsType<WorkOrderQRCodeItem> = [
    {
      title: 'Tipo',
      className: 'table-th',
      render: (_, item) => {
        const getLabel = () => {
          if (item.type === WorkOrderItemType.Part) {
            return item.isInternal ? 'Peça' : 'Peça Fornecida';
          }
          if (item.type === WorkOrderItemType.Service) {
            return item.isInternal ? 'Serviço' : 'Serviço Terceirizado';
          }
          return 'N/A';
        };

        return (
          <StatusTag color={workorderTypeColor[item.type]} label={getLabel()} />
        );
      },
    },
    {
      title: 'Descrição',
      className: 'table-th',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: 'Qtde./Tempo',
      className: 'table-th',
      dataIndex: 'quantity',
      align: 'right',
    },
    {
      title: 'Preço Uni.',
      className: 'table-th',
      dataIndex: 'unitPrice',
      align: 'right',
      render: (value) => formatterPrice(value),
    },
    {
      title: 'Desc.',
      className: 'table-th',
      dataIndex: 'discount',
      align: 'right',
      render: (value) => formatterPercent(value),
    },
    {
      title: 'Total',
      className: 'table-th',
      align: 'right',
      render: (_, item) => (
        <strong>
          {formatterPrice(
            (item.unitPrice - item.unitPrice * item.discount) * item.quantity,
          )}
        </strong>
      ),
    },
  ];

  const columnsObservations: ColumnsType<WorkOrderComplementObservations> = [
    {
      title: 'Descrição',
      className: 'table-th',
      dataIndex: 'description',
    },
    {
      title: 'Criada em',
      className: 'table-th',
      dataIndex: 'createdAt',
      render: (value) => moment(value).format('DD/MM/YY HH:MM'),
    },
  ];

  return (
    <>
      <Layout.Header className="app-header" style={{ textAlign: 'center' }}>
        <img className="image" src={logo} alt="logo" style={{ height: 25 }} />
      </Layout.Header>

      <div style={{ padding: 16, paddingTop: 0 }} className="workorder-root">
        <div
          className="workorder-container"
          style={{ paddingBottom: hasSignature ? 24 : 60 }}
        >
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Row gutter={[16, 16]}>
                <Col flex={1}>
                  <strong style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                    {complement?.workOrderNumberComplementNumber}
                  </strong>
                </Col>

                <Col>
                  <Group style={{ flexWrap: 'nowrap' }} gap={16}>
                    <Button
                      shape="circle"
                      icon={<ChevronLeft size={16} />}
                      className="workorder-action"
                      disabled={!hasPrev}
                      onClick={() =>
                        setCurrent((prev) => (prev > 0 ? prev - 1 : prev))
                      }
                    />

                    <Button
                      shape="circle"
                      icon={<ChevronRight size={16} />}
                      className="workorder-action"
                      disabled={!hasNext}
                      onClick={() =>
                        setCurrent((prev) =>
                          prev < (workorder?.complements.length || 1) - 1
                            ? prev + 1
                            : prev,
                        )
                      }
                    />
                  </Group>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              <Card>
                <Row gutter={[16, 16]}>
                  <Col span={24} sm={8} md={6} lg={4}>
                    <div className="company-image-root">
                      <Avatar
                        src={workorder?.company.logo}
                        className="company-image"
                      />
                    </div>
                  </Col>

                  <Col span={24} sm={16} md={18} lg={20}>
                    <div className="workorder-stack">
                      <big style={{ fontWeight: 700 }}>Dados da oficina</big>

                      {field(
                        'Razão social',
                        String(workorder?.company.name),
                        true,
                      )}

                      {field(
                        'Nome fantasia',
                        String(workorder?.company.nickName),
                        true,
                      )}
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={24}>
              <Card>
                <div className="workorder-stack">
                  <big style={{ fontWeight: 700 }}>Dados da O.S</big>

                  <Row gutter={[16, 16]}>
                    <Col span={24} sm={12} md={8} lg={6}>
                      {field(
                        'Nº OS',
                        String(complement?.workOrderNumberComplementNumber),
                      )}
                    </Col>

                    <Col span={24} sm={12} md={8} lg={6}>
                      {field(
                        'Situação',
                        complement?.status ? (
                          <StatusTag
                            color={workorderComplementColor[complement?.status]}
                            label={intl.formatMessage({
                              id: `workOrders.${complement?.status}`,
                            })}
                          />
                        ) : (
                          '-'
                        ),
                      )}
                    </Col>

                    <Col span={24} sm={12} md={8} lg={6}>
                      {field(
                        'Tipo',
                        complement?.type ? (
                          <Tag color="processing" style={{ margin: 0 }}>
                            {intl.formatMessage({
                              id: `workOrders.${complement.type}`,
                            })}
                          </Tag>
                        ) : (
                          '-'
                        ),
                      )}
                    </Col>

                    <Col span={24} sm={12} md={8} lg={6}>
                      {field('Orçament.', String(complement?.estimator.name))}
                    </Col>

                    <Col span={24} sm={12} md={8} lg={6}>
                      {field('Prisma', String(complement?.prisma || '-'))}
                    </Col>

                    <Col span={24} sm={12} md={8} lg={6}>
                      {field(
                        'Tipo Cliente',
                        complement?.customerType ? (
                          <Tag color="processing" style={{ margin: 0 }}>
                            {intl.formatMessage({
                              id: `workOrders.${complement.customerType}`,
                            })}
                          </Tag>
                        ) : (
                          '-'
                        ),
                      )}
                    </Col>

                    <Col span={24} sm={12} md={8} lg={6}>
                      {field(
                        'Entrada',
                        complement?.entryDate
                          ? moment(complement.entryDate).format(
                              'DD/MM/YY HH:MM',
                            )
                          : '-',
                      )}
                    </Col>

                    <Col span={24} sm={12} md={8} lg={6}>
                      {field(
                        'Autorização',
                        complement?.authorizationDate
                          ? moment(complement.authorizationDate).format(
                              'DD/MM/YY HH:MM',
                            )
                          : '-',
                      )}
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>

            <Col span={24}>
              <Card>
                <div className="workorder-stack">
                  <big style={{ fontWeight: 700 }}>Dados do cliente</big>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      {field(
                        'Nome do cliente',
                        String(workorder?.customer.name),
                        true,
                      )}
                    </Col>

                    <Col span={24}>
                      <div className="workoder-stack">
                        <strong>Assinatura</strong>
                        <div style={{ marginTop: 10 }}>
                          {hasSignature && (
                            <img
                              style={{ maxWidth: 300 }}
                              src={complement?.authorizedSignature}
                              alt="Assinatura do cliente"
                            />
                          )}
                          {!hasSignature && (
                            <Button onClick={signatureAction.open}>
                              Assinar e aprovar
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Divider style={{ marginTop: 8, marginBottom: 8 }} />

                  <big style={{ fontWeight: 700 }}>Dados do veículo</big>

                  <Row gutter={[16, 16]}>
                    <Col span={24} md={12}>
                      {field(
                        'Placa',
                        String(workorder?.vehicle?.licensePlate || '-'),
                      )}
                    </Col>

                    <Col span={24} md={12}>
                      {field(
                        'Combustível',
                        workorder?.vehicle?.fuelName || '-',
                      )}
                    </Col>

                    <Col span={24} md={12}>
                      {field(
                        'Ano Fabric./Modelo',
                        workorder?.vehicle
                          ? workorder?.vehicle?.yearFabrication +
                              '/' +
                              workorder?.vehicle?.yearModel
                          : '-',
                      )}
                    </Col>

                    <Col span={24} md={12}>
                      {field('Marca', String(workorder?.vehicle?.brand || '-'))}
                    </Col>

                    <Col span={24}>
                      {field(
                        'Modelo',
                        String(workorder?.vehicle?.model || '-'),
                        true,
                      )}
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>

            <Col span={24}>
              <Card className="workorder-items">
                <div className="workorder-stack">
                  <Row gutter={[16, 16]}>
                    <Col flex={1}>
                      <big style={{ fontWeight: 700 }}>Itens do orçamento</big>
                    </Col>
                    <Col>
                      <strong>
                        {formatterPrice(
                          items?.reduce(
                            (acc, item) =>
                              acc +
                              MathRound(
                                (item.unitPrice -
                                  item.unitPrice * item.discount) *
                                  item.quantity,
                                2,
                              ),
                            0,
                          ) || 0,
                        )}
                      </strong>
                    </Col>
                  </Row>

                  <Table
                    dataSource={complement?.items}
                    scroll={{ x: true }}
                    columns={[...columnsItems, ...columnRefuse]}
                    pagination={false}
                  />
                </div>
              </Card>
            </Col>

            {!!complement?.itemsRefused && (
              <Col span={24}>
                <Card className="workorder-items">
                  <div className="workorder-stack">
                    <Row gutter={[16, 16]}>
                      <Col flex={1}>
                        <big style={{ fontWeight: 700 }}>
                          Itens recusados pelo cliente
                        </big>
                      </Col>
                      <Col>
                        <strong>
                          {formatterPrice(
                            complement?.itemsRefused?.reduce(
                              (acc, item) =>
                                acc +
                                MathRound(
                                  (item.unitPrice -
                                    item.unitPrice * item.discount) *
                                    item.quantity,
                                  2,
                                ),
                              0,
                            ) || 0,
                          )}
                        </strong>
                      </Col>
                    </Row>

                    <Table
                      dataSource={complement?.itemsRefused}
                      scroll={{ x: true }}
                      columns={[
                        ...columnsItems,
                        {
                          title: 'Recusa',
                          className: 'table-th',
                          align: 'center',
                          render: (_, item) => (
                            <Popover
                              title="Motivo da recusa:"
                              content={
                                <div
                                  style={{
                                    maxWidth: 300,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {!!item.itemRejectedReason &&
                                    item.itemRejectedReason}

                                  {!item.itemRejectedReason &&
                                    'Motivo não informado'}
                                </div>
                              }
                            >
                              <MessageCircle size={16} />
                            </Popover>
                          ),
                        },
                      ]}
                      pagination={false}
                    />
                  </div>
                </Card>
              </Col>
            )}

            <Col span={24}>
              <Card className="workorder-items">
                <div className="workorder-stack">
                  <big style={{ fontWeight: 700 }}>
                    Observações do orçamento
                  </big>

                  <Table
                    dataSource={complement?.observations}
                    scroll={{ x: true }}
                    columns={columnsObservations}
                    pagination={false}
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {!hasSignature && (
        <div className="workorder-signature-footer">
          <Group
            style={{
              maxWidth: 1000,
              margin: '0 auto',
              height: '100%',
            }}
            position="between"
          >
            <strong style={{ lineHeight: 1 }}>
              Este orçamento ainda não foi aprovado
            </strong>

            {!hasSignature && (
              <Button type="primary" onClick={signatureAction.open}>
                Assinar e aprovar
              </Button>
            )}
          </Group>
        </div>
      )}

      <Modal
        title="Assinatura de aprovação"
        open={signatureOpen}
        onCancel={handleClose}
        okText="Assinar e aprovar"
        onOk={handleSubmit}
        confirmLoading={isLoading}
      >
        <p>
          Ao assinar o orçamento, vocè está autorizando a oficina a iniciar os
          serviços no seu veículo
        </p>

        <ReactSignatureCanvas
          canvasProps={{
            className: 'workorder-signature',
            style: { width: '100%', height: 300 },
          }}
          ref={sig}
        />

        <Button block danger onClick={() => sig.current?.clear()}>
          Limpar
        </Button>

        <Divider />

        <Group gap={8}>
          <Checkbox
            value={authorizeComplement}
            onChange={(event) => setAuthorizeComplement(event.target.checked)}
          >
            Autorizar orçamento?
          </Checkbox>

          <Tooltip title="Ao marcar o checkbox o orçamento será autorizado.">
            <InfoCircleOutlined />
          </Tooltip>
        </Group>
      </Modal>

      <ItemRefusePublicModal
        open={!!refuseItem}
        onCancel={() => setRefuseItem(undefined)}
        data={refuseItem}
      />
    </>
  );
}
